/**
 * The panel form's SECTION MODEL — sections declared as data, and the pieces
 * that read them (FORM_STYLE_GUIDE.md §12b).
 *
 * Every panel form describes itself as a list of sections, and the progress
 * pills, the collapsed summaries, the footer count and the payload all read
 * that one list. This module is where the reading happens, so a form file is
 * its field list plus its save call and nothing else — and so the five forms
 * on the system can't each grow a slightly different idea of what "filled"
 * means.
 *
 * ── A section ────────────────────────────────────────────────────────────
 *   key            stable id (React key)
 *   title, helper  the head
 *   fields[]       see below
 *   toggles[]      { name, title, subtext } → a `FormCheckList` of switches
 *   collapseWhen   SCOPE — open collapsed once that scope is full. Absent →
 *                  the section always opens. Never inferred.
 *   countScope     SCOPE the n/total pill counts. Default ALL.
 *   showCount      false where "none of these" IS the answer, so an empty
 *                  pill can't read as unanswered work
 *
 * ── A field ──────────────────────────────────────────────────────────────
 *   name, label, span (6 full / 3 half / 2 third), kind (see FIELD_KINDS)
 *   req            red asterisk + counts toward the footer
 *   optional       labelled "(optional)", counted by nothing
 *   visibleWhen(ctx)  gate on anything in ctx — the LOAN, other answers. A
 *                  hidden field is not rendered, not counted and NOT
 *                  required; that last part is what stops the footer sitting
 *                  at "1 required field left" with nothing on screen.
 *   kind "file"    FormFileDrop; the value is the upload-metadata array
 *                  (docGroup / docType / multiple / accept / hint on the field)
 *   render(props)  escape hatch for a one-off control; gets
 *                  { field, value, onChange }
 *
 * `ctx` is always `{ values, ...whatever the form needs }`.
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { FORM, formSx, px } from "../../theme/formStyles";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import {
  FormAddressField,
  FormCheckList,
  FormCheckRow,
  FormComboField,
  FormDateField,
  FormField,
  FormFileDrop,
  FormFieldGrid,
  FormGoogleAddressField,
  FormMoneyField,
  FormOptionChips,
  FormPercentField,
  FormPhoneField,
  FormSection,
  FormSelectField,
  FormSsnField,
  FormTextField,
} from "./FormPrimitives";

/** Which inputs a rule is talking about. One vocabulary, two consumers. */
export const SCOPE = { ALL: "all", REQUIRED: "required" };

/* ───────────────────────────── value rules ─────────────────────────────── */

export const isFilled = (v) => {
  if (v === null || v === undefined || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return !!(v.fullAddress || Object.keys(v).length);
  return true;
};

/** The fields this section actually shows, given the context. */
export const visibleFields = (section, ctx) =>
  (section.fields || []).filter((f) => !f.visibleWhen || f.visibleWhen(ctx));

/**
 * `filled / total` for one scope.
 *   ALL      — every counted input: non-`optional` fields plus the toggles.
 *              A toggle counts as answered only when it is ON.
 *   REQUIRED — only the `req` fields. Toggles are never required.
 * `total` is 0 for an empty scope, which is what keeps a section that asks
 * for nothing from reading as done.
 */
export const countedIn = (
  section,
  ctx,
  scope = section.countScope || SCOPE.ALL,
) => {
  const required = scope === SCOPE.REQUIRED;
  const fields = visibleFields(section, ctx).filter((f) =>
    required ? f.req : !f.optional,
  );
  const toggles = required ? [] : section.toggles || [];
  return {
    total: fields.length + toggles.length,
    filled:
      fields.filter((f) => isFilled(ctx.values?.[f.name])).length +
      toggles.filter((t) => ctx.values?.[t.name] === true).length,
  };
};

/** May this section OPEN collapsed? Only on a declared, satisfied scope. */
export const sectionIsComplete = (section, ctx) => {
  if (!section.collapseWhen) return false;
  const { filled, total } = countedIn(section, ctx, section.collapseWhen);
  return total > 0 && filled >= total;
};

/** Only a VISIBLE required field is required. */
export const requiredOf = (sections, ctx) =>
  sections.flatMap((s) => visibleFields(s, ctx).filter((f) => f.req));

export const countRequiredFilled = (required, values) =>
  required.filter((f) => isFilled(values?.[f.name])).length;

/* ─────────────────────── the collapsed summary ─────────────────────────── */

/** How many values a collapsed head shows before it says "+N more". */
const SUMMARY_MAX = 4;

const summaryValue = (field, values) => {
  const v = values?.[field.name];
  if (!isFilled(v)) return null;
  if (field.summary) return field.summary(v, values);
  switch (field.kind) {
    case "address":
    case "googleAddress":
      return v?.fullAddress || null;
    case "date":
      return formatDate(v, null);
    case "tel":
      return formatPhone(v);
    case "money":
      return `$${Number(v).toLocaleString()}`;
    case "percent":
    case "percentPresets":
      return `${Math.round(Number(v) * 100)}%`;
    case "number":
      return Number(v).toLocaleString();
    case "chipsMulti":
      return Array.isArray(v) ? v.join(", ") : `${v}`;
    case "file":
      return v.map((f) => f.file_display_name || f.name).join(", ");
    // Neither a paragraph nor an SSN belongs in a header.
    case "textarea":
    case "ssn":
      return null;
    default:
      return `${v}`;
  }
};

/**
 * What a collapsed section says in place of its description: the values it
 * holds, not a count — the count is already in the pill two inches to the
 * left, and the values are the thing that was folded away. An ON toggle
 * contributes its own title, which reads better than "true".
 */
export const summaryOf = (section, ctx) => {
  const parts = [
    ...visibleFields(section, ctx)
      .filter((f) => !f.optional)
      .map((f) => summaryValue(f, ctx.values)),
    ...(section.toggles || [])
      .filter((t) => ctx.values?.[t.name] === true)
      .map((t) => t.title),
  ].filter(Boolean);
  if (!parts.length) return "Nothing filled in yet";
  const shown = parts.slice(0, SUMMARY_MAX);
  const rest = parts.length - shown.length;
  return `${shown.join(" · ")}${rest ? ` · +${rest} more` : ""}`;
};

/* ───────────────────────────── payload rules ───────────────────────────── */

/** Drop the blanks — never post `""` where the RFF forms posted nothing. */
export const prune = (values) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(
      ([, v]) => v !== "" && v !== undefined && v !== null,
    ),
  );

/**
 * What `RffFormChanges` used to compute: the diff against the record. A field
 * that was blank and is blank again is not a change — a cleared date writes
 * `null` and a cleared text field `""`, while the record usually has no key.
 */
const isBlank = (v) => v === "" || v == null;

export const changedOnly = (values, initial) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(
      ([k, v]) =>
        !isEqualish(v, initial?.[k]) && !(isBlank(v) && isBlank(initial?.[k])),
    ),
  );

// Structural equality, enough for form values (scalars, arrays, plain objects).
const isEqualish = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

/**
 * Keys that must never reach a `$set` payload: `_id` is immutable and Mongo
 * aborts the whole update when it appears, and the id is already in the URL
 * path. Pass any others the model computes server-side.
 */
export const stripKeys = (values, keys = ["_id"]) => {
  const out = { ...(values || {}) };
  keys.forEach((k) => delete out[k]);
  return out;
};

/* ───────────────────────────── the renderer ────────────────────────────── */

export const FieldControl = ({ field, value, onChange }) => {
  if (field.render) return field.render({ field, value, onChange });
  const shared = {
    label: field.label,
    value,
    onChange,
    span: field.span,
    required: field.req,
    optional: field.optional,
    helperText: field.helperText,
    disabled: field.disabled,
  };
  switch (field.kind) {
    case "googleAddress":
      return (
        <FormGoogleAddressField
          {...shared}
          allowManualEntry={field.allowManualEntry}
        />
      );
    case "address":
      return <FormAddressField {...shared} />;
    case "select":
      return (
        <FormSelectField
          {...shared}
          options={field.options}
          placeholder={field.placeholder}
        />
      );
    case "combo":
      return <FormComboField {...shared} options={field.options} />;
    case "date":
      return <FormDateField {...shared} />;
    case "file":
      return (
        <FormFileDrop
          {...shared}
          multiple={field.multiple}
          accept={field.accept}
          docGroup={field.docGroup}
          docType={field.docType}
          prompt={field.placeholder}
          hint={field.hint}
        />
      );
    case "money":
      return <FormMoneyField {...shared} />;
    case "percent":
      return <FormPercentField {...shared} />;
    case "tel":
      return <FormPhoneField {...shared} />;
    case "ssn":
      return <FormSsnField {...shared} />;
    case "chips":
    case "chipsMulti":
      return (
        <FormField
          label={field.label}
          required={field.req}
          optional={field.optional}
          span={field.span}
          helperText={field.helperText}
        >
          <FormOptionChips
            options={field.options}
            value={value}
            onChange={onChange}
            multiple={field.kind === "chipsMulti"}
          />
        </FormField>
      );
    case "number":
      return (
        <FormTextField
          {...shared}
          type="number"
          suffix={field.suffix}
          onChange={(v) => onChange(v === "" ? null : Number(v))}
        />
      );
    case "textarea":
      return (
        <FormTextField
          {...shared}
          multiline
          minRows={field.minRows ?? 3}
          placeholder={field.placeholder}
        />
      );
    default:
      return (
        <FormTextField
          {...shared}
          type={field.kind === "email" ? "email" : undefined}
          placeholder={field.placeholder}
        />
      );
  }
};

FieldControl.propTypes = {
  field: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
};

/** One section: the grid, then its toggles, then anything the form adds. */
export const PanelSection = ({ section, ctx, setValue, n, children }) => {
  const count = countedIn(section, ctx);
  const fields = visibleFields(section, ctx);
  return (
    <FormSection
      block
      collapsible
      n={n}
      title={section.title}
      helper={section.helper}
      count={section.showCount === false ? undefined : count}
      complete={sectionIsComplete(section, ctx)}
      summary={summaryOf(section, ctx)}
    >
      <Flx column sx={{ gap: px(FORM.s.s5) }}>
        {fields.length ? (
          <FormFieldGrid>
            {fields.map((f) => (
              <FieldControl
                key={f.name}
                field={f}
                value={ctx.values?.[f.name]}
                onChange={(v) => setValue(f.name, v)}
              />
            ))}
          </FormFieldGrid>
        ) : null}

        {section.toggles?.length ? (
          <FormCheckList>
            {section.toggles.map((t) => (
              <FormCheckRow
                key={t.name}
                title={t.title}
                subtext={t.subtext}
                value={!!ctx.values?.[t.name]}
                onChange={(v) => setValue(t.name, v)}
              />
            ))}
          </FormCheckList>
        ) : null}

        {children}
      </Flx>
    </FormSection>
  );
};

PanelSection.propTypes = {
  section: PropTypes.object,
  ctx: PropTypes.object,
  setValue: PropTypes.func,
  n: PropTypes.number,
  children: PropTypes.node,
};

/** The whole stack. `numbered` turns on the 1-2-3 badges (off by default). */
export const PanelSections = ({ sections, ctx, setValue, numbered = false }) => (
  <Box>
    {sections.map((section, i) => (
      <PanelSection
        key={section.key}
        section={section}
        ctx={ctx}
        setValue={setValue}
        n={numbered ? i + 1 : undefined}
      >
        {section.extra ? section.extra({ ctx, setValue }) : null}
      </PanelSection>
    ))}
  </Box>
);

PanelSections.propTypes = {
  sections: PropTypes.array,
  ctx: PropTypes.object,
  setValue: PropTypes.func,
  numbered: PropTypes.bool,
};

/** A caption above a group inside a section ("Select all that apply"). */
export const PanelGroupLabel = ({ children }) => (
  <Txt sx={formSx.groupLabel}>{children}</Txt>
);

PanelGroupLabel.propTypes = { children: PropTypes.node };
